import React, { useState } from "react";
import {
  Grid,
  TextField,
  MenuItem,
  CircularProgress,
  Autocomplete,
  Checkbox,
  Box,
  Typography,
  Tooltip,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { IntegerInput, FloatInput } from "./NumericInputs";
import { inputStyle } from "./styles";
import { fieldOptions } from "./optionsHelper";
import { PROTOCOL_TYPES, MKTX_STRATEGY_TYPES } from "../../Constants/AppConstants";
import { tokens, withAlpha } from "../../Common/designTokens";
import { marketAxessStrategyInfoContent } from "./MarketAxessInfoContent";

const ALL_DEALER_OPTION = "ALL";

const OrderPrimaryFields = ({
  actualOrderData,
  errors,
  isFieldEditable,
  handleChange,
  handleProtocolChange,
  handleVenueChange,
  handlePriceBlur,
  isLoading,
  calcLoading,
  cusipError,
  source,
  readOnly,
  effectiveAccountIdentifiers,
  showTrumidOption,
  showMarketAxessOption,
  showMinInLabel,
  minQtyForLabel,
  isMarketAxess,
  venueDisabled,
  filterVenues,
  formatVenues,
  formatNumberWithCommas,
  bondDetails,
  isManualTraded,
  secondarySource,
}) => {
  const [isDealerDropdownOpen, setIsDealerDropdownOpen] = useState(false);
  const isLadderLevelSendToMarket = secondarySource === "ladder-level-send-to-market";
  const listName = String(actualOrderData.listName || "").trim();
  const listId = actualOrderData.listId ?? actualOrderData.mktxListId
    ?? (actualOrderData.tradingSessionSubId ? actualOrderData.tmidListId || actualOrderData.quoteReqId : null);
  const showListIdentity = actualOrderData.editRfqMode && listId != null;
  
  const renderAccountField = () => {
    // Allow editing account for allocateAccount even if CUSIP invalid / bondDetails missing
    const isAccountEditable = isFieldEditable("account")

    return (
      <Grid item xs={3}>
        <TextField
          fullWidth
          variant="filled"
          sx={inputStyle}
          InputProps={{ disableUnderline: true, readOnly: !isAccountEditable }}
          error={errors?.account}
          select={isAccountEditable}
          value={actualOrderData.account || ""}
          onChange={isAccountEditable ? handleChange : undefined}
          name="account"
          label="Account"
          disabled={!isAccountEditable}
        >
          {isAccountEditable &&
            effectiveAccountIdentifiers.map((identifier) => (
              <MenuItem key={identifier} value={identifier}>
                {identifier}
              </MenuItem>
            ))}
        </TextField>
      </Grid>
    );
  };

  // NEW: Check if MarketAxess with SEEK or PROVIDE strategy (hide venue)
  const isSeekOrProvideMarketAxess = 
    actualOrderData.protocol === PROTOCOL_TYPES.MARKETAXESS && 
    (actualOrderData.strategyType === MKTX_STRATEGY_TYPES.SEEK || 
     actualOrderData.strategyType === MKTX_STRATEGY_TYPES.PROVIDE);

  const normalizeVenueValueToArray = (venueValue) => {
    if (Array.isArray(venueValue)) {
      return venueValue
        .map((value) => String(value).trim())
        .filter((value) => value);
    }
    if (typeof venueValue === "string") {
      return venueValue
        .split(",")
        .map((value) => value.trim())
        .filter((value) => value);
    }
    return [];
  };

  const selectedVenueSet = new Set(normalizeVenueValueToArray(actualOrderData.venue));
  let selectedVenues = Array.from(selectedVenueSet);

  const baseDealerOptions = filterVenues(fieldOptions.venue || []);
  let dealerOptions =
    [PROTOCOL_TYPES.TRUMID_RFQ, PROTOCOL_TYPES.MARKETAXESS].includes(actualOrderData.protocol)
      ? [
          ALL_DEALER_OPTION,
          ...baseDealerOptions.filter((option) => option !== ALL_DEALER_OPTION),
        ]
      : [...baseDealerOptions];

  // Keep readonly/existing values visible even when options are not in latest API list.
  selectedVenues.forEach((value) => {
    if (!dealerOptions.includes(value)) {
      dealerOptions.push(value);
    }
  });

  if ([PROTOCOL_TYPES.TRUMID_RFQ, PROTOCOL_TYPES.MARKETAXESS].includes(actualOrderData.protocol)) {
    const hasAllSelected = selectedVenues.includes(ALL_DEALER_OPTION);
    const selectedDealers = selectedVenues.filter(
      (option) => option !== ALL_DEALER_OPTION
    );

    selectedVenues = hasAllSelected ? [ALL_DEALER_OPTION] : selectedDealers;
  }

  const handleDealerAutocompleteChange = (_event, newValues, reason, details) => {
    const normalizedSelection = Array.from(
      new Set((newValues || []).map((value) => String(value).trim()).filter((value) => value))
    );

    let nextValues = normalizedSelection;

    if ([PROTOCOL_TYPES.TRUMID_RFQ, PROTOCOL_TYPES.MARKETAXESS].includes(actualOrderData.protocol)) {
      const changedOption = details?.option;
      const hasAllSelected = normalizedSelection.includes(ALL_DEALER_OPTION);
      const selectedDealers = normalizedSelection.filter(
        (option) => option !== ALL_DEALER_OPTION
      );

      if (reason === "clear") {
        nextValues = [];
      } else if (changedOption === ALL_DEALER_OPTION) {
        // ALL is mutually exclusive with individual dealers.
        nextValues = hasAllSelected ? [ALL_DEALER_OPTION] : [];
      } else {
        // Keep named selections explicit. Trumid alone collapses a complete set at submit.
        nextValues = selectedDealers;
      }
    }

    handleVenueChange({
      target: {
        name: "venue",
        value: nextValues,
      },
    });
  };

  const dealerPreviewValue = formatVenues(selectedVenues);

  const dealerAutocompleteSx = {
    ...inputStyle,
    position: "relative",
    "& .MuiFilledInput-root.MuiAutocomplete-inputRoot": {
      ...inputStyle["& .MuiFilledInput-root"],
      paddingTop: "10px !important",
      paddingRight: "55px !important",
      paddingBottom: "0 !important",
      paddingLeft: "0 !important",
      minHeight: "auto",
      height: "auto",
      // Merge disabled styles to avoid losing child input styles while fixing background
      "&.Mui-disabled": {
        ...(inputStyle["& .MuiFilledInput-root"]["&.Mui-disabled"] || {}),
        backgroundColor: tokens.colors.white,
      }
    },
    "& .MuiAutocomplete-inputRoot .MuiAutocomplete-input": {
      padding: "14px 12px !important",
      minWidth: 0,
      textOverflow: "ellipsis",
      color: isDealerDropdownOpen ? tokens.colors.fontColour : "transparent",
      caretColor: isDealerDropdownOpen ? tokens.colors.fontColour : "transparent",
      cursor: venueDisabled ? "not-allowed" : "text",
    },
    "& .MuiAutocomplete-endAdornment": {
      top: "50%",
      transform: "translateY(-50%)",
      right: "8px",
      marginLeft: 0,
      display: "flex",
      gap: 0,
    },
    "& .MuiAutocomplete-clearIndicator": {
      padding: "4px",
      marginRight: 0,
    },
    "& .MuiAutocomplete-popupIndicator": {
      padding: "4px",
    },
    "& .MuiAutocomplete-inputRoot": {
      flexWrap: "nowrap",
    },
  };

  return (
    <>
      {showListIdentity && (
        <Grid item xs={3}>
          <TextField
            fullWidth
            variant="filled"
            label={listName ? "List Name" : "List ID"}
            value={listName || listId}
            disabled
            sx={inputStyle}
            InputProps={{ disableUnderline: true }}
          />
        </Grid>
      )}
      {!isLadderLevelSendToMarket && (
        <Grid item xs={3}>
          <TextField
            variant="filled"
            label="Identifier"
            fullWidth
            name="cusip"
            value={actualOrderData.cusip || ""}
            onChange={isFieldEditable("cusip") ? handleChange : () => {}}
            sx={inputStyle}
            placeholder="Enter Identifier (e.g. 914302ER1)"
            error={isManualTraded ? false : errors?.cusip || !!cusipError}
            disabled={!isFieldEditable("cusip")}
            InputProps={{
              endAdornment: isLoading && <CircularProgress size={20} />,
              disableUnderline: true,
              readOnly: !isFieldEditable("cusip"),
            }}
          />
        </Grid>
      )}
      <Grid item xs={3}>
        <TextField
          fullWidth
          select
          name="side"
          value={actualOrderData.side || ""}
          onChange={isFieldEditable("side") ? handleChange : () => {}}
          variant="filled"
          label="Side"
          error={errors?.side}
          disabled={!isFieldEditable("side")}
          sx={inputStyle}
          InputProps={{ disableUnderline: true }}
        >
          {fieldOptions.side.map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      
      {renderAccountField()}
      
      {!isLadderLevelSendToMarket && (
        <>
          <Grid item xs={3}>
            <IntegerInput
              name="qty"
              value={actualOrderData.qty}
              onChange={isFieldEditable("qty") ? handleChange : () => {}}
              placeholder={isMarketAxess ? "Multiple of 1000" : "Enter quantity"}
              label={
                isMarketAxess ? (
                  <span>
                    Quantity
                    <span style={{ opacity: 0.6, marginLeft: 6 }}>
                      (Multiple of 1000)
                    </span>
                  </span>
                ) : showMinInLabel ? (
                  <span>
                    Quantity
                    <span style={{ opacity: 0.6, marginLeft: 6 }}>
                      (Min: {formatNumberWithCommas(minQtyForLabel)})
                    </span>
                  </span>
                ) : (
                  "Quantity"
                )
              }
              disabled={!isFieldEditable("qty")}
              error={errors?.qty}
              maxValue={10000000}
              sx={inputStyle}
              step={isMarketAxess ? 1000 : 1}
            />
          </Grid>
          <Grid item xs={3}>
            <FloatInput
              name="price"
              value={actualOrderData.price || ""}
              onChange={isFieldEditable("price") ? handleChange : () => {}}
              placeholder="Enter price"
              label="Price"
              disabled={!isFieldEditable("price")}
              error={errors?.price}
              maxValue={150}
              sx={inputStyle}
              onBlur={handlePriceBlur}
              InputProps={{
                endAdornment: calcLoading && <CircularProgress size={20} />,
              }}
            />
          </Grid>
        </>
      )}
      <Grid item xs={3}>
        <TextField
          fullWidth
          select
          name="protocol"
          value={
            (source === "inventory" || source === "buyOrderEntry")
              ? PROTOCOL_TYPES.CLOB
              : (actualOrderData.protocol || "")
          }
          onChange={isFieldEditable("protocol") ? handleProtocolChange : () => {}}
          variant="filled"
          label="Protocol"
          error={errors?.protocol}
          disabled={!isFieldEditable("protocol")}
          sx={inputStyle}
          InputProps={{ disableUnderline: true }}
        >
          <MenuItem value={PROTOCOL_TYPES.CLOB}>{PROTOCOL_TYPES.CLOB}</MenuItem>
          <MenuItem value={PROTOCOL_TYPES.RFQ}>{PROTOCOL_TYPES.RFQ}</MenuItem>
          {showTrumidOption && (
            <MenuItem value={PROTOCOL_TYPES.TRUMID_RFQ}>Trumid RFQ</MenuItem>
          )}
          {showMarketAxessOption && (
            <MenuItem value={PROTOCOL_TYPES.MARKETAXESS}>MarketAxess RFQ</MenuItem>
          )}
        </TextField>
      </Grid>

      {/* NEW: Strategy Type - Show only for MarketAxess */}
      {actualOrderData.protocol === PROTOCOL_TYPES.MARKETAXESS && (
        <Grid item xs={3}>
          <TextField
            fullWidth
            select
            name="strategyType"
            value={actualOrderData.strategyType ?? fieldOptions.strategyType[0]}
            onChange={isFieldEditable("strategyType") ? handleChange : () => {}}
            variant="filled"
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                Strategy Type
                <Tooltip
                  title={marketAxessStrategyInfoContent}
                  arrow
                  componentsProps={{
                    tooltip: { sx: strategyTooltipStyles.tooltip },
                    arrow: { sx: strategyTooltipStyles.arrow },
                  }}
                >
                  <InfoOutlinedIcon 
                    fontSize="small" 
                    sx={{ 
                      cursor: 'pointer',
                    }} 
                  />
                </Tooltip>
              </Box>
            }
            error={errors?.strategyType}
            disabled={!isFieldEditable("strategyType")}
            sx={inputStyle}
            InputProps={{
              disableUnderline: true,
            }}
          >
            {fieldOptions.strategyType.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      )}

      <Grid item xs={3} sx={{ display: isSeekOrProvideMarketAxess ? 'none' : 'block' }}>
        {(!isFieldEditable("venue") && 
          ((typeof actualOrderData.venue === "string" && actualOrderData.venue.includes(",")) || 
           source === "inventory")) ? (
          <TextField
            fullWidth
            variant="filled"
            label="Dealer"
            value={
              Array.isArray(actualOrderData.venue)
                ? actualOrderData.venue.join(", ")
                : (actualOrderData.venue || "")
            }
            disabled
            sx={{
              ...inputStyle,
              "& .MuiInputBase-input": {
                overflow: "auto",
                whiteSpace: "nowrap",
                textOverflow: "initial",
                scrollbarWidth: "thin",
                "&::-webkit-scrollbar": { height: "4px" },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "rgba(255, 255, 255, 0.2)",
                  borderRadius: "2px",
                },
              },
            }}
            InputProps={{ disableUnderline: true }}
          />
        ) : (actualOrderData.protocol === PROTOCOL_TYPES.RFQ || 
             actualOrderData.protocol === PROTOCOL_TYPES.TRUMID_RFQ ||
             actualOrderData.protocol === PROTOCOL_TYPES.MARKETAXESS) ? (
          <Autocomplete
            multiple
            disableCloseOnSelect
            options={dealerOptions}
            value={selectedVenues}
            openOnFocus
            onChange={isFieldEditable("venue") ? handleDealerAutocompleteChange : undefined}
            onOpen={() => {
              setIsDealerDropdownOpen(true);
            }}
            onClose={() => {
              setIsDealerDropdownOpen(false);
            }}
            disabled={venueDisabled}
            isOptionEqualToValue={(option, value) => option === value}
            getOptionLabel={(option) => option || ""}
            renderOption={(props, option, { selected }) => {
              const hideCheckbox =
                [PROTOCOL_TYPES.TRUMID_RFQ, PROTOCOL_TYPES.MARKETAXESS].includes(actualOrderData.protocol) &&
                option === ALL_DEALER_OPTION;

              return (
                <Box
                  component="li"
                  {...props}
                  sx={{
                    pl: "10px",
                    py: 1,
                    display: "flex",
                    alignItems: "center",
                    fontSize: "16px",
                    borderRadius: 0,
                    "&:hover": { bgcolor: withAlpha(tokens.palette.primary, 0.08) },
                    "&.Mui-focused": { bgcolor: withAlpha(tokens.palette.primary, 0.08) },
                    "&[aria-selected='true']": {
                      bgcolor: "transparent",
                    },
                  }}
                >
                  {!hideCheckbox && (
                    <Checkbox
                      checked={selected}
                      sx={{ width: "20px", height: "20px", padding: 0, marginRight: "8px", borderRadius: 0 }}
                      disableRipple
                    />
                  )}
                  <Typography
                    sx={{
                      fontSize: "16px",
                      fontWeight: selected ? "bold" : "normal",
                      color: selected ? tokens.colors.fontColour : tokens.colors.fontColour85,
                      lineHeight: "20px",
                    }}
                  >
                    {option}
                  </Typography>
                </Box>
              );
            }}
            renderTags={() => null}
            renderInput={(params) => (
              <Box sx={{ position: "relative" }}>
                <TextField
                  {...params}
                  fullWidth
                  variant="filled"
                  label="Dealer"
                  error={errors?.venue}
                  sx={inputStyle}
                  InputLabelProps={{
                    ...params.InputLabelProps,
                    shrink: true,
                  }}
                  InputProps={{
                    ...params.InputProps,
                    disableUnderline: true,
                  }}
                />
                {!isDealerDropdownOpen && !!dealerPreviewValue && (
                  <Typography
                    sx={{
                      position: "absolute",
                      left: "12px",
                      right: "55px",
                      top: "50%",
                      transform: "translateY(calc(-50% + 10px))",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      pointerEvents: "none",
                      color: venueDisabled ? tokens.colors.disabledFontColour : tokens.colors.fontColour,
                      fontSize: "0.95rem",
                      lineHeight: "1.25rem",
                    }}
                  >
                    {dealerPreviewValue}
                  </Typography>
                )}
              </Box>
            )}
            sx={dealerAutocompleteSx}
          />
        ) : (
          <TextField
            fullWidth
            select
            name="venue"
            value={typeof actualOrderData.venue === "string" ? actualOrderData.venue : ""}
            onChange={isFieldEditable("venue") ? handleChange : () => {}}
            variant="filled"
            label="Dealer"
            error={errors?.venue}
            disabled={!isFieldEditable("venue")}
            sx={inputStyle}
            InputProps={{ disableUnderline: true }}
          >
            {filterVenues(fieldOptions.venue).map((option) => (
              <MenuItem key={option} value={option}>{option}</MenuItem>
            ))}
          </TextField>
        )}
      </Grid>
    </>
  );
};

export default OrderPrimaryFields;

const strategyTooltipStyles = {
  tooltip: {
    bgcolor: tokens.colors.darkgray,
    color: tokens.colors.white,
    border: `1px solid ${tokens.colors.bodyBorderColor50}`,
    boxShadow: "0px 10px 30px rgba(0, 0, 0, 0.25)",
    fontSize: "0.85rem",
    maxWidth: 620,
    p: 1,
  },
  arrow: {
    color: tokens.colors.darkgray,
  },
};
